import type { FrequencyCap } from './types';

/** All suppression state lives in the visitor's own localStorage — the CMS never sets a cookie on
 * the visitor's behalf (see PublicFormsCorsFilter's doc comment for why: no Access-Control-Allow-
 * Credentials is ever sent, so a CMS-set cookie wouldn't round-trip here anyway). */
function storageKey(formId: string): string {
  return `cms_lf_state_${formId}`;
}

interface FormState {
  lastShownAt?: number;
  converted?: boolean;
}

function readState(formId: string): FormState {
  try {
    const raw = localStorage.getItem(storageKey(formId));
    return raw ? (JSON.parse(raw) as FormState) : {};
  } catch {
    return {};
  }
}

function writeState(formId: string, state: FormState): void {
  try {
    localStorage.setItem(storageKey(formId), JSON.stringify(state));
  } catch {
    // Best-effort — a form that can't remember its own suppression state just shows every time.
  }
}

export function canShow(formId: string, cap: FrequencyCap): boolean {
  const state = readState(formId);
  if (cap.suppressAfterConvert && state.converted) return false;

  if (cap.reshowAfterHours != null && state.lastShownAt) {
    const elapsedHours = (Date.now() - state.lastShownAt) / (1000 * 60 * 60);
    if (elapsedHours < cap.reshowAfterHours) return false;
  }
  return true;
}

export function recordShown(formId: string): void {
  const state = readState(formId);
  writeState(formId, { ...state, lastShownAt: Date.now() });
}

export function recordConverted(formId: string): void {
  const state = readState(formId);
  writeState(formId, { ...state, converted: true });
}
